import { useState, useEffect } from 'react';
import { Navigation } from '../../components/Navigation';
import { FloatingBackButton } from '../../components/FloatingBackButton';
import { useAuthStore } from '../../store/authStore';
import { supabase } from '../../lib/supabase';
import { resumeService } from '../../lib/resumeGenerationService';
import { resumeTemplateService } from '../../lib/resumeTemplateService';
import { resumeExportService } from '../../lib/resumeExportService';
import { FileText, Edit2, Trash2, Star, Calendar, Plus, Mail, Check, Heart, FileDown } from 'lucide-react';
import { Link } from 'react-router-dom';

interface Resume {
  id: string;
  title: string;
  content: any;
  is_primary: boolean;
  created_at: string;
  updated_at: string;
}

export function MyResumes() {
  const { user } = useAuthStore();
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingResumeId, setDeletingResumeId] = useState<string | null>(null);
  const [renamingResumeId, setRenamingResumeId] = useState<string | null>(null);
  const [newResumeName, setNewResumeName] = useState('');
  const [favoriteResumes, setFavoriteResumes] = useState<Set<string>>(new Set());
  const [defaultResumeId, setDefaultResumeId] = useState<string | null>(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedResumeForEmail, setSelectedResumeForEmail] = useState<Resume | null>(null);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [emailMessage, setEmailMessage] = useState('');

  useEffect(() => {
    if (user) {
      loadResumes();
      loadFavorites();
      loadDefaultResume();
    }
  }, [user]);

  const loadResumes = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await resumeService.getUserResumes(user.id);
      setResumes(data);
    } catch (error) {
      console.error('Error loading resumes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFavorites = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('resume_metadata')
      .select('resume_id, is_favorite')
      .eq('is_favorite', true);

    if (data) {
      setFavoriteResumes(new Set(data.map(d => d.resume_id)));
    }
  };

  const loadDefaultResume = async () => {
    if (!user) return;
    const defaultId = await resumeTemplateService.getDefaultResume(user.id);
    setDefaultResumeId(defaultId);
  };

  const handleSetPrimary = async (resumeId: string) => {
    if (!user) return;

    const { error } = await supabase
      .from('resumes')
      .update({ is_primary: false })
      .eq('user_id', user.id);

    if (!error) {
      await supabase
        .from('resumes')
        .update({ is_primary: true })
        .eq('id', resumeId);

      loadResumes();
    }
  };

  const handleDeleteResume = async (resumeId: string) => {
    if (!user) return;

    if (deletingResumeId === resumeId) {
      const { error } = await supabase
        .from('resumes')
        .delete()
        .eq('id', resumeId);

      if (!error) {
        setDeletingResumeId(null);
        loadResumes();
      }
    } else {
      setDeletingResumeId(resumeId);
      setTimeout(() => setDeletingResumeId(null), 3000);
    }
  };

  const handleDownloadResume = async (resume: Resume, format: 'pdf' | 'docx' | 'txt' = 'pdf') => {
    if (!user) return;

    try {
      let url: string;
      let filename: string;

      switch (format) {
        case 'pdf':
          url = await resumeExportService.exportAsPDF(
            resume.id,
            resume.content,
            {},
            { primary: '#2563eb', text: '#1f2937' },
            user.id
          );
          filename = `${resume.title}.pdf`;
          break;
        case 'docx':
          url = await resumeExportService.exportAsDOCX(resume.id, resume.content, user.id);
          filename = `${resume.title}.docx`;
          break;
        default:
          url = await resumeExportService.exportAsTXT(resume.id, resume.content, user.id);
          filename = `${resume.title}.txt`;
      }

      resumeExportService.downloadFile(url, filename);
    } catch (error) {
      console.error('Error downloading resume:', error);
      alert('Failed to download resume');
    }
  };

  const handleRenameResume = async (resumeId: string) => {
    if (!newResumeName.trim()) return;

    const success = await resumeTemplateService.updateResumeName(resumeId, newResumeName);
    if (success) {
      await loadResumes();
      setRenamingResumeId(null);
      setNewResumeName('');
    }
  };

  const handleToggleFavorite = async (resumeId: string) => {
    const success = await resumeTemplateService.toggleFavoriteResume(resumeId);
    if (success) {
      await loadFavorites();
    }
  };

  const handleSetDefault = async (resumeId: string) => {
    if (!user) return;
    const success = await resumeTemplateService.setDefaultResume(user.id, resumeId);
    if (success) {
      setDefaultResumeId(resumeId);
      await handleSetPrimary(resumeId);
    }
  };

  const handleEmailResume = async () => {
    if (!user || !selectedResumeForEmail || !recipientEmail) return;

    try {
      const share = await resumeTemplateService.shareResumeViaEmail(
        user.id,
        selectedResumeForEmail.id,
        recipientEmail,
        emailMessage
      );

      if (share) {
        alert(`Resume shared successfully with ${recipientEmail}`);
        setEmailModalOpen(false);
        setSelectedResumeForEmail(null);
        setRecipientEmail('');
        setEmailMessage('');
      } else {
        alert('Failed to share resume');
      }
    } catch (error) {
      console.error('Error sharing resume:', error);
      alert('Failed to share resume');
    }
  };

  // TODO: Add resume text preview feature
  const _generateResumeText = (resume: Resume): string => {
    const content = resume.content;
    let text = '';

    text += `${content.personal?.fullName || ''}\n`;
    text += `${content.personal?.email || ''} | ${content.personal?.phone || ''}\n`;
    text += `${content.personal?.location || ''}\n\n`;

    if (content.summary) {
      text += `PROFESSIONAL SUMMARY\n${content.summary}\n\n`;
    }

    if (content.experience && content.experience.length > 0) {
      text += `EXPERIENCE\n`;
      content.experience.forEach((exp: any) => {
        text += `\n${exp.title} at ${exp.company}\n`;
        text += `${exp.startDate} - ${exp.endDate || 'Present'}\n`;
        if (exp.bullets) {
          exp.bullets.forEach((bullet: string) => {
            text += `• ${bullet}\n`;
          });
        }
      });
      text += '\n';
    }

    if (content.education && content.education.length > 0) {
      text += `EDUCATION\n`;
      content.education.forEach((edu: any) => {
        text += `${edu.degree} in ${edu.field}\n`;
        text += `${edu.school}, ${edu.graduationDate}\n\n`;
      });
    }

    if (content.skills) {
      text += `SKILLS\n`;
      const allSkills = [...(content.skills.technical || []), ...(content.skills.soft || []), ...(content.skills.other || [])];
      text += allSkills.join(', ') + '\n\n';
    }

    if (content.certifications && content.certifications.length > 0) {
      text += `CERTIFICATIONS\n`;
      content.certifications.forEach((cert: string) => {
        text += `• ${cert}\n`;
      });
    }

    return text;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <>
      <Navigation />
      <FloatingBackButton />
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 p-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">My Resumes</h1>
              <p className="text-gray-600 mt-1">Manage your professional resumes</p>
            </div>
            <Link
              to="/jobseeker/resume-builder"
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-medium hover:from-orange-600 hover:to-amber-600 transition"
            >
              <Plus className="w-5 h-5" />
              Build New Resume
            </Link>
          </div>

          {resumes.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">No resumes yet</h2>
              <p className="text-gray-600 mb-6">Create your first AI-powered resume to get started</p>
              <Link
                to="/jobseeker/resume-builder"
                className="inline-block px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-medium hover:from-orange-600 hover:to-amber-600 transition"
              >
                Build Resume
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {resumes.map((resume) => {
                const isDeleting = deletingResumeId === resume.id;
                const isRenaming = renamingResumeId === resume.id;
                const isFavorite = favoriteResumes.has(resume.id);
                const isDefault = defaultResumeId === resume.id;

                return (
                  <div key={resume.id} className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-200 hover:border-orange-300 transition">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center">
                          <FileText className="w-6 h-6 text-white" />
                        </div>
                        {resume.is_primary && (
                          <div className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-800 rounded-lg text-xs font-semibold">
                            <Star className="w-3 h-3 fill-current" />
                            Primary
                          </div>
                        )}
                        {isDefault && (
                          <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-lg text-xs font-semibold">
                            <Check className="w-3 h-3" />
                            Default
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => handleToggleFavorite(resume.id)}
                        className={`p-2 rounded-lg transition ${
                          isFavorite
                            ? 'bg-red-100 text-red-600'
                            : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                        }`}
                      >
                        <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
                      </button>
                    </div>

                    {isRenaming ? (
                      <div className="mb-4">
                        <input
                          type="text"
                          value={newResumeName}
                          onChange={(e) => setNewResumeName(e.target.value)}
                          className="w-full px-3 py-2 border-2 border-cyan-500 rounded-lg focus:outline-none"
                          placeholder="Enter new name"
                          autoFocus
                        />
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={() => handleRenameResume(resume.id)}
                            className="px-4 py-2 bg-cyan-500 text-white rounded-lg font-medium hover:bg-cyan-600 transition"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setRenamingResumeId(null);
                              setNewResumeName('');
                            }}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-xl font-bold text-gray-900">{resume.title}</h3>
                        <button
                          onClick={() => {
                            setRenamingResumeId(resume.id);
                            setNewResumeName(resume.title);
                          }}
                          className="text-cyan-600 hover:text-cyan-700 text-sm font-medium"
                        >
                          Rename
                        </button>
                      </div>
                    )}

                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                      <Calendar className="w-4 h-4" />
                      <span>Updated {new Date(resume.updated_at).toLocaleDateString()}</span>
                    </div>

                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <select
                            onChange={(e) => handleDownloadResume(resume, e.target.value as 'pdf' | 'docx' | 'txt')}
                            className="w-full px-3 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition appearance-none cursor-pointer"
                            defaultValue=""
                          >
                            <option value="" disabled>Download as...</option>
                            <option value="pdf">PDF</option>
                            <option value="docx">DOCX</option>
                            <option value="txt">TXT</option>
                          </select>
                          <FileDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                        </div>
                        <button
                          onClick={() => {
                            setSelectedResumeForEmail(resume);
                            setEmailModalOpen(true);
                          }}
                          className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition flex items-center justify-center gap-2"
                        >
                          <Mail className="w-4 h-4" />
                          Email
                        </button>
                      </div>

                      <div className="flex gap-2">
                        {!isDefault && (
                          <button
                            onClick={() => handleSetDefault(resume.id)}
                            className="flex-1 px-3 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition flex items-center justify-center gap-2"
                          >
                            <Check className="w-4 h-4" />
                            Set Default
                          </button>
                        )}
                        <Link
                          to={`/jobseeker/resume-builder?resumeId=${resume.id}`}
                          className="flex-1 px-3 py-2 bg-cyan-500 text-white rounded-lg font-medium hover:bg-cyan-600 transition flex items-center justify-center gap-2"
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit
                        </Link>
                      </div>

                      <button
                        onClick={() => handleDeleteResume(resume.id)}
                        className={`w-full px-3 py-2 rounded-lg font-medium transition flex items-center justify-center gap-2 ${
                          isDeleting
                            ? 'bg-red-500 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        <Trash2 className="w-4 h-4" />
                        {isDeleting ? 'Click to Confirm' : 'Delete'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {emailModalOpen && selectedResumeForEmail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Email Resume</h2>
            <p className="text-gray-600 mb-6">
              Send "{selectedResumeForEmail.title}" to someone via email
            </p>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Recipient Email *
                </label>
                <input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:outline-none"
                  placeholder="employer@company.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Message (Optional)
                </label>
                <textarea
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:outline-none"
                  placeholder="Add a personal message..."
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setEmailModalOpen(false);
                  setSelectedResumeForEmail(null);
                  setRecipientEmail('');
                  setEmailMessage('');
                }}
                className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleEmailResume}
                disabled={!recipientEmail}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-bold hover:from-cyan-600 hover:to-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Send Email
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
